"use client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { usePropertyChat } from "@/contexts/property-chat-context";
import { useUser } from "@/hooks/useUser";
import { authenticatedFetch } from "@/lib/auth";
import { shareContent } from "@/lib/share";
import { MessageSquare, Share2 } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { Zillow } from "../svgs/zillow";

interface PropertyActionCardProps {
  property: { url?: string; address?: string; id?: string } | null;
  propertyId: string | null;
  slug: string;
  isLimited?: boolean;
  masterkeySavings?: number;
}

export function PropertyActionCard({
  property,
  propertyId,
  slug,
  isLimited,
}: PropertyActionCardProps) {
  const { user } = useUser();
  const { openChat } = usePropertyChat();
  const router = useRouter();
  const { toast } = useToast();
  const [isSaving, setIsSaving] = useState(false);

  const handleChatClick = () => {
    openChat();
  };

  const handleShare = async () => {
    const url = window.location.href;
    await shareContent({ url });
  };

  const handleGetStartedClick = async (
    event: React.MouseEvent<HTMLAnchorElement>,
  ) => {
    const shouldAttemptSave = !isLimited && user;

    if (shouldAttemptSave) {
      event.preventDefault();

      if (!propertyId) {
        toast({
          title: "Error",
          description: "Property ID is missing.",
          variant: "destructive",
        });
        return;
      }

      setIsSaving(true);
      try {
        const response = await authenticatedFetch(
          `/api/journeys/save-analyzed-property/${propertyId}`,
          {
            method: "POST",
          },
        );
        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.error || "Failed to save property");
        }

        toast({
          title: result.alreadySaved
            ? "Property Already Saved"
            : "Property Saved",
          description: "Redirecting to your properties...",
        });
        router.push("/app/properties");
      } catch (error) {
        toast({
          title: "Error Saving Property",
          description:
            error instanceof Error
              ? error.message
              : "An unknown error occurred.",
          variant: "destructive",
        });
      } finally {
        setIsSaving(false);
      }
    } else {
      console.log(
        "[PropertyActionCard] Letting Link navigate (logged out or limited). Href:",
        event.currentTarget.href,
      );
    }
  };

  const getHref = () => {
    if (isLimited) {
      return `/login?from=/?analyze=${encodeURIComponent(property?.url || "")}`;
    }
    if (user) {
      return "/app/properties";
    }
    return `/login?from=/properties/${slug}`;
  };

  return (
    <Card className="overflow-hidden">
      <div className="p-6 space-y-6">
        <div className="space-y-4">
          <Link
            href={getHref()}
            onClick={handleGetStartedClick}
            className="block"
            aria-disabled={isSaving}
            style={{ pointerEvents: isSaving ? "none" : "auto" }}
          >
            <Button
              size="xl"
              className="w-full rounded-full font-bold bg-canary text-black hover:bg-canary/90"
              disabled={isSaving}
            >
              {isSaving
                ? "Saving..."
                : isLimited
                  ? "Get Full Analysis →"
                  : "Save Property →"}
            </Button>
          </Link>

          <div className="flex items-center justify-center gap-2">
            <div className="flex -space-x-1">
              {[1, 2, 3, 4].map((num) => (
                <Image
                  key={num}
                  src={`/images/people/${num}.png`}
                  width={16}
                  height={16}
                  alt=""
                  className="h-4 w-4 rounded-full border border-white/80"
                />
              ))}
            </div>
            <span className="text-xs text-gray-600">Join 1,000+ buyers</span>
          </div>
        </div>
      </div>

      <div
        className="grid divide-x border-t"
        style={{ gridTemplateColumns: isLimited ? "1fr 1fr" : "1fr 1fr 1fr" }}
      >
        {!isLimited && (
          <Button
            variant="ghost"
            className="rounded-none h-12 text-muted-foreground hover:text-foreground hover:bg-gray-50"
            onClick={handleChatClick}
          >
            <MessageSquare className="w-4 h-4 mr-2" />
            Chat
          </Button>
        )}
        {property?.url && (
          <Link
            href={property.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex h-12 items-center justify-center gap-2 text-muted-foreground hover:text-foreground hover:bg-gray-50"
          >
            <Zillow />
            <span>Zillow</span>
          </Link>
        )}
        <Button
          variant="ghost"
          className="rounded-none h-12 text-muted-foreground hover:text-foreground hover:bg-gray-50"
          onClick={handleShare}
        >
          <Share2 className="w-4 h-4 mr-2" />
          Share
        </Button>
      </div>
    </Card>
  );
}
